function timeToMinutes(timeStr) {
  if (!timeStr) return 0;
  const [hours, minutes] = String(timeStr).slice(0, 5).split(':').map(Number);
  return (hours || 0) * 60 + (minutes || 0);
}

function minutesToTime(minutes) {
  const hours = Math.floor(minutes / 60) % 24;
  const mins = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}

export function formatTimeAMPM(timeStr) {
  if (!timeStr) return '';
  const [hours, minutes] = String(timeStr).slice(0, 5).split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${String(minutes).padStart(2, '0')} ${period}`;
}

function rangesOverlap(startA, endA, startB, endB) {
  return startA < endB && endA > startB;
}

function fitsInRange(startMin, endMin, rangeStart, rangeEnd) {
  if (rangeEnd < rangeStart) {
    const startInRange = startMin >= rangeStart || startMin <= rangeEnd;
    const endInRange = endMin <= rangeEnd || (endMin >= rangeStart && endMin <= 24 * 60);
    return startInRange && endInRange;
  }
  return startMin >= rangeStart && endMin <= rangeEnd;
}

/**
 * Returns whether the demo time fits teacher availability.
 * When ok is false, exceptionReason explains exactly why an override is needed.
 */
export function checkDemoTimeAgainstTeacherAvailability({
  startTime,
  durationMinutes = 30,
  availableTimeRanges = [],
  teacherAvailability = null,
  bookedBatches = [],
  bookedDemos = [],
  dayLabel = 'this day',
  skipWorkHours = false,
}) {
  if (!startTime) {
    return { ok: true, exceptionReason: null };
  }

  const startMin = timeToMinutes(startTime);
  const endMin = startMin + Number(durationMinutes || 30);
  const endTime = minutesToTime(endMin);
  const slots = teacherAvailability?.slots || [];

  if (!skipWorkHours) {
    if (!slots.length) {
      return {
        ok: false,
        exceptionReason: `Teacher has no work hours configured for ${dayLabel}.`,
      };
    }

    const workSlots = slots.map((s) => ({
      start: timeToMinutes(s.start_time),
      end: timeToMinutes(s.end_time),
      label: `${formatTimeAMPM(s.start_time)} – ${formatTimeAMPM(s.end_time)}`,
    }));

    const workHoursSummary = workSlots.map((s) => s.label).join('; ');

    const containingWorkSlot = workSlots.find((s) => {
      if (s.end < s.start) {
        return startMin >= s.start || startMin <= s.end;
      }
      return startMin >= s.start && startMin < s.end;
    });

    if (!containingWorkSlot) {
      return {
        ok: false,
        exceptionReason: `Start time ${formatTimeAMPM(startTime)} is outside the teacher's work hours on ${dayLabel} (${workHoursSummary}).`,
      };
    }

    if (containingWorkSlot.end >= containingWorkSlot.start && endMin > containingWorkSlot.end) {
      return {
        ok: false,
        exceptionReason: `The ${durationMinutes}-minute demo would end at ${formatTimeAMPM(endTime)}, but the work slot ends at ${formatTimeAMPM(minutesToTime(containingWorkSlot.end))}.`,
      };
    }
  }

  if (!skipWorkHours) {
    for (const batch of bookedBatches) {
      const bStart = timeToMinutes(batch.start_time);
      const bEnd = timeToMinutes(batch.end_time);
      if (rangesOverlap(startMin, endMin, bStart, bEnd)) {
        const name = batch.batch_name || 'Batch';
        return {
          ok: false,
          exceptionReason: `Time overlaps existing batch "${name}" (${formatTimeAMPM(batch.start_time)} – ${formatTimeAMPM(batch.end_time)}).`,
        };
      }
    }
  }

  for (const booked of bookedDemos) {
    const dStart = timeToMinutes(booked.scheduled_time);
    const dEnd = dStart + Number(booked.duration_minutes || 30);
    if (rangesOverlap(startMin, endMin, dStart, dEnd)) {
      return {
        ok: false,
        exceptionReason: `Time overlaps another demo/trial (${formatTimeAMPM(booked.scheduled_time)}, ${booked.duration_minutes || 30} min).`,
      };
    }
  }

  if (availableTimeRanges.length > 0) {
    const inAvailable = availableTimeRanges.some((range) =>
      fitsInRange(startMin, endMin, timeToMinutes(range.start_time), timeToMinutes(range.end_time))
    );
    if (!inAvailable) {
      const freeSummary = availableTimeRanges
        .map((r) => `${formatTimeAMPM(r.start_time)} – ${formatTimeAMPM(r.end_time)}`)
        .join('; ');
      return {
        ok: false,
        exceptionReason: `Selected time ${formatTimeAMPM(startTime)} – ${formatTimeAMPM(endTime)} is not within a free slot. Available gaps after batches/demos: ${freeSummary || 'none'}.`,
      };
    }
  }

  return { ok: true, exceptionReason: null };
}

export function buildScheduleExceptionNote(reason, userNote) {
  const parts = [`[Schedule exception] ${reason}`];
  if (userNote?.trim()) parts.push(`Override note: ${userNote.trim()}`);
  return parts.join(' ');
}

export { timeToMinutes, minutesToTime };
